import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { BadgeCheck, Building2, FileText, LogOut, Ticket, Trophy, Users } from "lucide-react";
import { BACKEND_URL } from "../lib/api";
import { AssetImage } from "../components/AssetImage";

const KEY = "pfp_affiliate_token";
const money = (c) => `A$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const day = (s) => (s ? new Date(s).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—");

const api = () => axios.create({ baseURL: BACKEND_URL, headers: { Authorization: `Bearer ${localStorage.getItem(KEY) || ""}` } });

const download = async (url, name) => {
  const { data } = await api().get(url, { responseType: "blob" });
  const href = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = href;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(href), 4000);
};

const Card = ({ label, value, sub, testId }) => (
  <div data-testid={testId} className="gold-card rounded-xl p-4 flex flex-col gap-1">
    <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300/80">{label}</div>
    <div className="font-display text-2xl text-amber-50">{value}</div>
    {sub && <div className="text-xs text-slate-400">{sub}</div>}
  </div>
);

const Login = ({ onIn }) => {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/affiliate/login`, { email, password: pw });
      localStorage.setItem(KEY, data.access_token);
      onIn();
    } catch (x) {
      const d = x?.response?.data?.detail;
      setErr(typeof d === "string" ? d : d?.detail || "Email or password is not right");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-950">
      <form onSubmit={submit} className="gold-card rounded-2xl p-7 w-full max-w-sm flex flex-col gap-4" data-testid="affiliate-login">
        <div className="flex flex-col items-center gap-2">
          <AssetImage id="logo_emblem" width={78} />
          <div className="font-display text-lg text-amber-50">Affiliate sign-in</div>
          <p className="text-xs text-slate-400 text-center">Partner clubs and businesses only. Your dashboard shows your players, your rebate and your invoices.</p>
        </div>
        <label className="text-xs text-slate-300">Email
          <input data-testid="affiliate-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required
            className="mt-1 w-full rounded-lg bg-black/50 border border-slate-700 px-3 py-2 text-sm text-slate-100" />
        </label>
        <label className="text-xs text-slate-300">Password
          <input data-testid="affiliate-password" value={pw} onChange={(e) => setPw(e.target.value)} type="password" required
            className="mt-1 w-full rounded-lg bg-black/50 border border-slate-700 px-3 py-2 text-sm text-slate-100" />
        </label>
        {err && <div data-testid="affiliate-login-error" className="text-xs text-red-300">{err}</div>}
        <button data-testid="affiliate-login-submit" disabled={busy} type="submit"
          className="btn-gold rounded-full py-2 text-sm font-display tracking-wider btn-lift disabled:opacity-50">{busy ? "SIGNING IN…" : "SIGN IN"}</button>
        <p className="text-[10px] text-slate-500 text-center">Accounts are issued by Player's Room — The NOTFAR Group Pty Ltd, ABN 47 666 127 804.</p>
      </form>
    </div>
  );
};

const ordinal = (n) => `${n}${["th", "st", "nd", "rd"][n % 100 > 10 && n % 100 < 14 ? 0 : Math.min(n % 10, 4) % 4] || "th"}`;
const chips = (n) => (n || 0).toLocaleString();

const Fact = ({ l, v, testId }) => (
  <div className="flex flex-col gap-0.5" data-testid={testId}>
    <span className="text-[10px] uppercase tracking-[0.2em] text-amber-300/70">{l}</span>
    <span className="text-sm text-amber-50">{v}</span>
  </div>
);

// A satellite we have built for this club: everything they need to check, then the one tick that puts it live.
export const EventCard = ({ ev, prefix, onApprove, busy }) => {
  const [note, setNote] = useState(ev.note || "");
  const sat = ev.affiliate_event;
  return (
    <div data-testid={`affiliate-event-${ev.event_id}`} className="gold-card rounded-xl p-5 flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display text-lg text-amber-50">{ev.name}</div>
          <div className="text-xs text-slate-400">{sat ? "Your satellite — open to everyone, listed as your game" : "Listed as your game"} · code prefix <span className="font-mono text-amber-200">{ev.affiliate_prefix || prefix}</span></div>
        </div>
        {sat && (ev.approved
          ? <span data-testid={`affiliate-event-approved-${ev.event_id}`} className="rounded-full border border-emerald-400/60 px-3 py-1 text-[10px] tracking-[0.18em] text-emerald-200">APPROVED {day(ev.approved_at)}</span>
          : <span data-testid={`affiliate-event-awaiting-${ev.event_id}`} className="rounded-full border border-amber-400/60 px-3 py-1 text-[10px] tracking-[0.18em] text-amber-200">AWAITING YOUR APPROVAL</span>)}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-white/5 pt-4">
        <Fact l="Date & time" v={`${(ev.times || []).join(" · ") || "—"} ${ev.tz_label || ""}`} testId={`affiliate-event-when-${ev.event_id}`} />
        <Fact l="Buy-in" v={ev.free ? "Freeroll (free entry)" : `${chips(ev.buy_in)} + ${chips(ev.fee)} fee`} testId={`affiliate-event-buyin-${ev.event_id}`} />
        <Fact l="Starting stack" v={`${chips(ev.starting_stack)} chips`} />
        <Fact l="Levels" v={`${ev.level_minutes} min · late reg through L${ev.late_reg_levels}`} />
        <Fact l="Game" v={`${ev.game} · ${ev.mode === "REBUY" ? "re-buy" : "freezeout"}`} />
        <Fact l="Table size" v={`${ev.max_seats}-max`} />
        <Fact l="Field" v={`min ${ev.min_entrants}${ev.max_entrants ? ` · cap ${ev.max_entrants}` : ""}`} />
        <Fact l="Players paid" v={ev.paid_places || "—"} testId={`affiliate-event-places-${ev.event_id}`} />
      </div>

      {!!(ev.levels || []).length && (
        <div className="flex flex-col gap-1" data-testid={`affiliate-event-blinds-${ev.event_id}`}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/70">Blind structure ({ev.level_minutes} minutes a level)</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-xs text-slate-300">
            {ev.levels.map((l) => <span key={l.level}>L{l.level} {chips(l.sb)}/{chips(l.bb)}</span>)}
          </div>
        </div>
      )}

      {!!(ev.prizes || []).length && (
        <div className="flex flex-col gap-1" data-testid={`affiliate-event-prizes-${ev.event_id}`}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/70">Prize redemption codes — one per paid place</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {ev.prizes.map((p) => (
              <div key={p.place} data-testid={`affiliate-event-code-${ev.event_id}-${p.place}`} className="rounded-lg bg-black/40 border border-amber-400/15 px-3 py-2 flex items-center gap-2">
                <span className="text-[11px] text-amber-300 font-display w-8">{ordinal(p.place)}</span>
                <span className="font-mono text-sm text-amber-100 tracking-wider">{p.code}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {sat && (
        <div className="border-t border-white/5 pt-4 flex flex-col gap-3">
          <label className="flex items-start gap-2 text-sm text-amber-100">
            <input data-testid={`affiliate-event-confirm-${ev.event_id}`} type="checkbox" className="mt-1" disabled={busy}
              checked={!!ev.approved} onChange={(e) => onApprove(ev, e.target.checked, note)} />
            <span>I confirm this is correct — the buy-in, the date and time, the blind structure and the winner codes.
              <span className="block text-[11px] text-slate-400">Players cannot see this satellite until this is ticked. Untick it and leave a note if something needs changing.</span></span>
          </label>
          <div className="flex items-end gap-2 flex-wrap">
            <label className="flex flex-col gap-1 flex-1 min-w-[16rem]">
              <span className="text-[10px] uppercase tracking-[0.2em] text-amber-300/70">Note for Player's Room (optional)</span>
              <input data-testid={`affiliate-event-note-${ev.event_id}`} value={note} onChange={(e) => setNote(e.target.value)} maxLength={400}
                placeholder="e.g. make it 7:30pm and pay 5 places"
                className="rounded-lg bg-black/50 border border-slate-700 px-3 py-2 text-sm text-slate-100" />
            </label>
            <button data-testid={`affiliate-event-send-note-${ev.event_id}`} disabled={busy || !note.trim()}
              onClick={() => onApprove(ev, false, note)}
              className="rounded-full px-4 py-2 text-xs border border-slate-600 hover:border-amber-400 disabled:opacity-40">Request a change</button>
          </div>
          {ev.approved_at && <div className="text-[11px] text-emerald-300/80" data-testid={`affiliate-event-stamp-${ev.event_id}`}>Approved {day(ev.approved_at)} — it is live in the lobby.</div>}
        </div>
      )}
    </div>
  );
};

const TABS = [["overview", "Overview", Building2], ["players", "My players", Users], ["invoices", "Invoices", FileText],
  ["events", "Our tournaments", Trophy], ["codes", "Winner codes", Ticket]];

export default function AffiliatePage() {
  const [token, setTokenState] = useState(() => localStorage.getItem(KEY));
  const [data, setData] = useState(null);
  const [events, setEvents] = useState(null);
  const [codes, setCodes] = useState(null);
  const [tab, setTab] = useState("overview");
  const [err, setErr] = useState("");
  const [busyCode, setBusyCode] = useState("");
  const [busyEvent, setBusyEvent] = useState("");

  const load = useCallback(async () => {
    try {
      const c = api();
      const [d, e, k] = await Promise.all([c.get("/api/affiliate/dashboard"), c.get("/api/affiliate/tournaments"), c.get("/api/affiliate/codes")]);
      setData(d.data);
      setEvents(e.data);
      setCodes(k.data);
    } catch (x) {
      if (x?.response?.status === 401) { localStorage.removeItem(KEY); setTokenState(null); }
      else setErr("Could not load your dashboard — try again shortly.");
    }
  }, []);

  useEffect(() => { if (token) load(); }, [token, load]);

  const toggleRedeem = async (c) => {
    setBusyCode(c.code);
    try {
      await api().post(`/api/affiliate/codes/${encodeURIComponent(c.code)}/redeem`, { redeemed: !c.redeemed });
      const k = await api().get("/api/affiliate/codes");
      setCodes(k.data);
    } catch (x) {
      setErr("Could not update that code — try again shortly.");
    } finally { setBusyCode(""); }
  };

  const approveEvent = async (ev, approved, note) => {
    setBusyEvent(ev.event_id);
    try {
      await api().post(`/api/affiliate/tournaments/${ev.event_id}/approve`, { approved, note: note || "" });
      const e = await api().get("/api/affiliate/tournaments");
      setEvents(e.data);
    } catch (x) {
      setErr("Could not save that — try again shortly.");
    } finally { setBusyEvent(""); }
  };

  if (!token) return <Login onIn={() => setTokenState(localStorage.getItem(KEY))} />;
  if (!data) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-400 text-sm" data-testid="affiliate-loading">{err || "Loading your dashboard…"}</div>;

  const a = data.affiliate;
  const e = data.earnings;
  const signOut = () => { localStorage.removeItem(KEY); setTokenState(null); setData(null); setTab("overview"); };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 px-6 py-6 flex flex-col gap-6" data-testid="affiliate-portal">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <AssetImage id="logo_emblem" width={52} />
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300">Affiliate portal</div>
            <h1 className="font-display text-xl text-amber-50" data-testid="affiliate-name">{a.business_name}</h1>
            <div className="text-xs text-slate-400">Referral code <span className="font-mono text-amber-200" data-testid="affiliate-code">{a.referral_code}</span>
              {a.code_prefix && <> · winner-code prefix <span className="font-mono text-amber-200">{a.code_prefix}</span></>}</div>
          </div>
        </div>
        <button data-testid="affiliate-signout" onClick={signOut} className="rounded-full px-4 py-2 text-xs border border-slate-600 hover:border-slate-400 flex items-center gap-2"><LogOut className="w-3.5 h-3.5" /> Sign out</button>
      </header>

      <nav className="flex gap-2 flex-wrap">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} data-testid={`affiliate-tab-${k}`} onClick={() => setTab(k)}
            className={`rounded-full px-4 py-2 text-xs font-display tracking-wider flex items-center gap-2 ${tab === k ? "bg-amber-500 text-slate-900" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </nav>

      {!!(events?.awaiting || []).length && (
        <button data-testid="affiliate-awaiting-banner" onClick={() => setTab("events")}
          className="rounded-xl border border-amber-400/50 bg-amber-500/10 px-4 py-3 text-left text-sm text-amber-100 hover:bg-amber-500/20 transition-colors">
          <span className="font-display tracking-wider">{events.awaiting.length} satellite{events.awaiting.length > 1 ? "s" : ""} waiting on your approval</span>
          <span className="block text-xs text-amber-200/70">Check the buy-in, date, blinds and winner codes, then tick the box — players only see it once you do.</span>
        </button>
      )}

      {tab === "overview" && (
        <section className="grid gap-4 md:grid-cols-4" data-testid="affiliate-overview">
          <Card testId="card-rebate" label={`Your ${e.percent}% rebate`} value={money(e.rebate_cents)} sub={`on ${money(e.gross_cents)} of player purchases`} />
          <Card testId="card-unbilled" label="Not yet invoiced" value={money(e.unbilled_cents)} sub={`${money(e.invoiced_cents)} invoiced to date`} />
          <Card testId="card-last-payout" label="Last payout" value={data.last_payout ? money(data.last_payout.total_cents) : "—"}
            sub={data.last_payout ? `${data.last_payout.invoice_no} · paid ${day(data.last_payout.paid_at)}` : "No payment made yet"} />
          <Card testId="card-next-payout" label="Next payout"
            value={data.next_payout ? money(data.next_payout.total_cents) : money(e.unbilled_cents)}
            sub={data.next_payout
              ? `${data.next_payout.invoice_no} · due ${day(data.next_payout.due_at || data.next_payout.created_at)}`
              : `Weekly run · ${data.schedule?.weekday || "Wednesday"} ${day(data.schedule?.next_run)} (estimate)`} />
          <Card testId="card-players" label="Players you referred" value={e.player_count} sub="registered with your code" />
          <div className="gold-card rounded-xl p-4 md:col-span-3 text-xs text-slate-300 flex flex-col gap-1">
            <div className="flex items-center gap-2 text-amber-200"><BadgeCheck className="w-4 h-4" /> How this is worked out</div>
            <p>{e.percent}% of what the players who signed up with <span className="font-mono text-amber-200">{a.referral_code}</span> actually pay for chips, net of refunds and chargebacks (affiliate T&Cs clause 3). Player-to-player referrals are not included — official affiliates only.</p>
            <p data-testid="affiliate-schedule-note">Payment runs are <span className="text-amber-200">weekly, every {data.schedule?.weekday || "Wednesday"}</span>, covering the seven days to the day before. Next run: <span className="text-amber-200">{day(data.schedule?.next_run)}</span>.</p>
            <p>Player's Room issues a Recipient Created Tax Invoice (RCTI) for each payment run — {a.gst_registered ? "GST is added because you are registered for GST." : "no GST is added (you are recorded as not registered for GST)."} Recipient: The NOTFAR Group Pty Ltd, ABN 47 666 127 804.</p>
          </div>
        </section>
      )}

      {tab === "players" && (
        <section className="gold-card rounded-xl overflow-hidden" data-testid="affiliate-players">
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-[10px] uppercase tracking-[0.2em] text-amber-300/80">
              <tr><th className="text-left p-3">Player</th><th className="text-left p-3">Joined</th><th className="text-right p-3">Purchases</th><th className="text-right p-3">Spent</th><th className="text-right p-3">Your {e.percent}%</th></tr>
            </thead>
            <tbody>
              {e.players.map((p) => (
                <tr key={p.user_id} data-testid={`affiliate-player-${p.user_id}`} className="border-t border-white/5">
                  <td className="p-3">{p.name}</td><td className="p-3 text-slate-400">{day(p.joined)}</td>
                  <td className="p-3 text-right">{p.purchases}</td><td className="p-3 text-right">{money(p.spend_cents)}</td>
                  <td className="p-3 text-right text-amber-200">{money(p.rebate_cents)}</td>
                </tr>
              ))}
              {!e.players.length && <tr><td colSpan={5} className="p-4 text-center text-slate-400 text-xs">No players have used your code yet.</td></tr>}
            </tbody>
          </table>
        </section>
      )}

      {tab === "invoices" && (
        <section className="gold-card rounded-xl overflow-hidden" data-testid="affiliate-invoices">
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-[10px] uppercase tracking-[0.2em] text-amber-300/80">
              <tr><th className="text-left p-3">Invoice</th><th className="text-left p-3">Period</th><th className="text-right p-3">Rebate</th><th className="text-right p-3">GST</th><th className="text-right p-3">Total</th><th className="text-left p-3">Status</th><th className="text-left p-3">Reference</th><th className="text-right p-3">Tax invoice</th></tr>
            </thead>
            <tbody>
              {data.invoices.map((p) => (
                <tr key={p.payout_id} data-testid={`affiliate-invoice-${p.invoice_no}`} className="border-t border-white/5">
                  <td className="p-3 font-mono text-amber-200">{p.invoice_no}</td>
                  <td className="p-3 text-slate-400">{day(p.period_start)} – {day(p.period_end)}</td>
                  <td className="p-3 text-right">{money(p.rebate_cents)}</td><td className="p-3 text-right">{money(p.gst_cents)}</td>
                  <td className="p-3 text-right text-amber-200">{money(p.total_cents)}</td>
                  <td className="p-3">{p.status === "paid" ? <span className="text-emerald-300">Paid {day(p.paid_at)}</span> : <span className="text-amber-300">Scheduled {day(p.due_at)}</span>}</td>
                  <td className="p-3 text-slate-400 font-mono text-xs">{p.payment_ref || "—"}</td>
                  <td className="p-3 text-right">
                    <button data-testid={`affiliate-invoice-pdf-${p.invoice_no}`} onClick={() => download(`/api/affiliate/invoices/${p.payout_id}/pdf`, `${p.invoice_no}.pdf`)}
                      className="rounded-full px-3 py-1 text-[11px] bg-white/5 hover:bg-white/15 text-amber-100">PDF</button>
                  </td>
                </tr>
              ))}
              {!data.invoices.length && <tr><td colSpan={8} className="p-4 text-center text-slate-400 text-xs">No invoices yet — one is created for every scheduled payment run.</td></tr>}
            </tbody>
          </table>
        </section>
      )}

      {tab === "events" && (
        <section className="flex flex-col gap-4" data-testid="affiliate-events">
          {(events?.events || []).map((ev) => (
            <EventCard key={ev.event_id} ev={ev} prefix={a.code_prefix} onApprove={approveEvent} busy={busyEvent === ev.event_id} />
          ))}
          {(events?.tables || []).map((t) => (
            <div key={t.table_id} data-testid={`affiliate-table-${t.table_id}`} className="gold-card rounded-xl p-4">
              <div className="font-display text-amber-50">{t.name}</div>
              <div className="text-xs text-slate-400">{t.format} · {t.game}{t.sng ? ` · buy-in ${t.sng.buy_in?.toLocaleString()}` : ""}</div>
            </div>
          ))}
          {!events?.events?.length && !events?.tables?.length && <p className="text-xs text-slate-400">No tournaments or satellites are flagged as yours yet — ask Player's Room to tag your next event.</p>}
        </section>
      )}

      {tab === "codes" && (
        <section className="gold-card rounded-xl overflow-hidden" data-testid="affiliate-codes">
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-[10px] uppercase tracking-[0.2em] text-amber-300/80">
              <tr><th className="text-left p-3">Code</th><th className="text-left p-3">Event</th><th className="text-left p-3">Winner</th><th className="text-right p-3">Place</th><th className="text-left p-3">Prize</th><th className="text-left p-3">Redeemed</th><th className="text-right p-3">At your venue</th></tr>
            </thead>
            <tbody>
              {(codes?.codes || []).map((c) => (
                <tr key={c.code} data-testid={`affiliate-wincode-${c.code}`} className="border-t border-white/5">
                  <td className="p-3 font-mono text-amber-200">{c.code}</td><td className="p-3">{c.event}</td>
                  <td className="p-3">{c.winner_name}</td><td className="p-3 text-right">{c.place}</td>
                  <td className="p-3 text-slate-300">{c.prize || "—"}</td>
                  <td className="p-3">
                    {c.redeemed
                      ? <span className="text-emerald-300" data-testid={`affiliate-redeemed-${c.code}`}>Yes · {day(c.redeemed_at)}{c.redeemed_source === "staff" ? " (Player's Room)" : ""}</span>
                      : <span className="text-slate-400">Not yet</span>}
                    {(c.history?.length || 0) > 1 && <div className="text-[10px] text-slate-500">changed {c.history.length} times</div>}
                  </td>
                  <td className="p-3 text-right">
                    <button data-testid={`affiliate-redeem-${c.code}`} disabled={busyCode === c.code}
                      onClick={() => toggleRedeem(c)}
                      className={`rounded-full px-3 py-1 text-[11px] disabled:opacity-50 ${c.redeemed ? "bg-white/5 hover:bg-white/15 text-slate-300" : "btn-gold font-display"}`}>
                      {busyCode === c.code ? "…" : c.redeemed ? "Undo" : "Mark redeemed"}</button>
                  </td>
                </tr>
              ))}
              {!codes?.codes?.length && <tr><td colSpan={7} className="p-4 text-center text-slate-400 text-xs">No winner codes issued yet. Winners redeem their code at your venue.</td></tr>}
            </tbody>
          </table>
          <p className="p-3 text-[11px] text-slate-500 border-t border-white/5">Mark a code redeemed when the winner collects their prize at your venue — Player's Room sees the same status straight away. Tapped it by mistake? Undo puts it back, and every change is kept on the record.</p>
        </section>
      )}
    </div>
  );
}
